import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Sparkles } from 'lucide-react';
import { liteApi } from '@/utils/lite/liteApi';

/**
 * Modal shown when the user has exhausted their daily quota.
 * Two paths:
 *  - Email unlock (+5 generations today)
 *  - Waitlist for paid HIPAA tier
 */
export default function RateLimitGate({ open, onClose, used, limit, onUnlocked }) {
  const [mode, setMode] = useState('unlock'); // 'unlock' | 'waitlist' | 'success'
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleUnlock = async () => {
    setError('');
    setSubmitting(true);
    try {
      const res = await liteApi.emailUnlock(email);
      setMessage(res?.message || 'Unlocked!');
      setMode('success');
      // Persist email locally so subsequent calls include it.
      try { localStorage.setItem('playfirst_lite_email', email); } catch {}
      onUnlocked?.(email);
    } catch (e) {
      setError(e.message || 'Could not unlock. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWaitlist = async () => {
    setError('');
    setSubmitting(true);
    try {
      await liteApi.waitlist(email, 'US');
      setMessage("You're on the list. We'll be in touch when the HIPAA tier launches.");
      setMode('success');
    } catch (e) {
      setError(e.message || 'Could not join waitlist. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="lite-rate-limit-modal" className="sm:max-w-md">
        {mode !== 'success' && (
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {mode === 'unlock'
                ? `You've used your ${limit ?? 5} free generations today`
                : 'Get notified when HIPAA mode launches'}
            </DialogTitle>
            <DialogDescription>
              {mode === 'unlock'
                ? 'Enter your email to unlock 5 more today, or join the waitlist for our upcoming US tier with full Business Associate Agreement coverage.'
                : 'We\'ll email you when our upgraded US tier with full BAA coverage launches for US clients with PHI.'}
            </DialogDescription>
          </DialogHeader>
        )}

        {mode === 'success' && (
          <div className="py-6 text-center" data-testid="lite-gate-success">
            <Sparkles className="w-10 h-10 mx-auto mb-3 text-primary" />
            <p className="text-stone-700">{message}</p>
            <Button
              onClick={onClose}
              className="mt-4 rounded-full"
              data-testid="lite-gate-close-btn"
            >
              Continue
            </Button>
          </div>
        )}

        {mode !== 'success' && (
          <div className="space-y-3 py-2">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                type="email"
                placeholder="you@clinic.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-9"
                data-testid="lite-gate-email-input"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <p className="text-xs text-stone-500">
              We use email only for usage tracking and (if you opt in) HIPAA tier launch updates. No spam, easy unsubscribe.
            </p>
          </div>
        )}

        {mode === 'unlock' && (
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setMode('waitlist')}
              className="rounded-full"
              data-testid="lite-switch-waitlist-btn"
            >
              I want HIPAA mode
            </Button>
            <Button
              onClick={handleUnlock}
              disabled={!email || submitting}
              className="rounded-full"
              data-testid="lite-gate-unlock-btn"
            >
              {submitting ? 'Unlocking…' : 'Unlock 5 more'}
            </Button>
          </DialogFooter>
        )}

        {mode === 'waitlist' && (
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setMode('unlock')}
              className="rounded-full"
              data-testid="lite-switch-unlock-btn"
            >
              Back
            </Button>
            <Button
              onClick={handleWaitlist}
              disabled={!email || submitting}
              className="rounded-full"
              data-testid="lite-gate-waitlist-btn"
            >
              {submitting ? 'Joining…' : 'Join waitlist'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
